import { z } from "zod";

/**
 * Mirrors apps/api/src/lib/env.ts: validated once at import time so a
 * misconfigured build fails loudly rather than at the first request.
 */
const envSchema = z.object({
  VITE_API_BASE_URL: z.string().min(1).default("/api/dashboard"),
  /**
   * Optional, matching the API: when the backend has API_KEY set, requests
   * must carry it; when it does not, this stays unset. Inlined into the
   * bundle either way — a deployment discriminator, never a secret.
   */
  VITE_API_KEY: z.string().min(1).optional(),
});

export type DashboardEnv = z.infer<typeof envSchema>;

const FALLBACK: DashboardEnv = {
  VITE_API_BASE_URL: "/api/dashboard",
  VITE_API_KEY: undefined,
};

/**
 * An unset build arg reaches the bundle as the EMPTY STRING, not undefined —
 * `docker build --build-arg VITE_API_KEY=` inlines `""`. Empty means "not
 * configured", so normalise it before validating; otherwise
 * `.min(1).optional()` rejects it and the whole app fails to boot on any
 * deployment running without the X-Api-Key gate. That failed as a blank page.
 */
const optional = (value: string | undefined) =>
  value && value.trim() ? value : undefined;

export function parseEnv(raw: {
  VITE_API_BASE_URL?: string;
  VITE_API_KEY?: string;
}): { env: DashboardEnv; error: string | null } {
  const parsed = envSchema.safeParse({
    VITE_API_BASE_URL: optional(raw.VITE_API_BASE_URL),
    VITE_API_KEY: optional(raw.VITE_API_KEY),
  });
  return parsed.success
    ? { env: parsed.data, error: null }
    : {
        env: FALLBACK,
        error: parsed.error.issues
          .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
          .join("\n"),
      };
}

// Read each key explicitly. Vite string-replaces `import.meta.env.VITE_X`
// occurrences at build time; it does not materialise the whole object, so
// spreading or iterating it yields nothing in a production build.
const parsed = parseEnv({
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_API_KEY: import.meta.env.VITE_API_KEY,
});

export const env = parsed.env;

/**
 * Non-null when the configuration is unusable. main.tsx renders this instead of
 * mounting the app — throwing here would leave a BLANK WHITE PAGE with nothing
 * to explain it, the worst possible failure mode for a tool a clinician is
 * trying to sign in to.
 */
export const envError: string | null = parsed.error;

if (envError) console.error(`Invalid dashboard environment:\n${envError}`);
